import { type ParsedUrlQuery } from 'node:querystring';

import { QueryParamNames } from '~/constants/search';

/** sessionStorage key for the showcases table search restored after closing the editor. The VALUE is
 * frozen — it outlives a deploy, so the `/share-links` → `/showcases` URL migration must not touch it. */
export const SHOWCASES_LIST_SEARCH_STASH_KEY = 'shareLinksListSearch';

/** Router query objects may carry undefined values; ParsedUrlQuery does not. */
type RouterQueryInput = { [key: string]: string | string[] | undefined };

const withoutUndefinedQueryValues = (query: RouterQueryInput): ParsedUrlQuery =>
  Object.fromEntries(
    Object.entries(query).filter((entry): entry is [string, string | string[]] => entry[1] !== undefined),
  );

export const stashShowcasesListSearch = (term: string | string[] | undefined) => {
  if (typeof term === 'string' && term.trim()) {
    sessionStorage.setItem(SHOWCASES_LIST_SEARCH_STASH_KEY, term);
  } else {
    sessionStorage.removeItem(SHOWCASES_LIST_SEARCH_STASH_KEY);
  }
};

export const consumeStashedShowcasesListSearch = (): string | undefined => {
  const term = sessionStorage.getItem(SHOWCASES_LIST_SEARCH_STASH_KEY);
  sessionStorage.removeItem(SHOWCASES_LIST_SEARCH_STASH_KEY);
  return term ?? undefined;
};

/**
 * Builds the `/showcases?showcase=<id>` query for opening the collection editor. Drops list-page
 * `q` so the editor gallery does not inherit the table filter; stash the list search separately
 * before navigation so it can be restored on close.
 */
export const buildShowcaseOpenQuery = (query: RouterQueryInput, collectionId: string): ParsedUrlQuery => {
  const { [QueryParamNames.Search]: _listSearch, [QueryParamNames.Showcase]: _showcase, ...rest } = query;

  return withoutUndefinedQueryValues({
    ...rest,
    [QueryParamNames.Showcase]: collectionId,
  });
};

/** Query for `/showcases` after closing the editor — restores a stashed list search if present. */
export const buildShowcasesQueryAfterClose = (query: RouterQueryInput): ParsedUrlQuery => {
  const stashedListSearch = consumeStashedShowcasesListSearch();
  const { [QueryParamNames.Showcase]: _showcase, [QueryParamNames.Search]: _editorSearch, ...rest } = query;

  if (stashedListSearch) {
    return withoutUndefinedQueryValues({
      ...rest,
      [QueryParamNames.Search]: stashedListSearch,
    });
  }

  return withoutUndefinedQueryValues(rest);
};
